import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Zap, RefreshCw, CheckCircle, PenLine, MessageSquare, Briefcase, Wand2, Search, LayoutGrid } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useABVariant } from '../utils/abTest';
import { useDocumentMeta } from '../utils/useDocumentMeta';
import SocialStrip from '../components/SocialStrip';
import ResumeStudioShowcase from '../components/ResumeStudioShowcase';
import ScrollToStudioHint from '../components/ScrollToStudioHint';

// Motion variants
const fadeInUp = { opacity: 0, y: 20 };
const fadeInUpVisible = { opacity: 1, y: 0 };
const fadeInRight = { opacity: 0, x: 20 };
const heroAnimate = { opacity: 1, y: 0 };
const heroTransition = { duration: 0.6 };
const heroRightAnimate = { opacity: 1, x: 0 };
const heroRightTransition = { duration: 0.6, delay: 0.2 };
const scrollTransition = { duration: 0.5 };
const scrollViewport = { once: true };

// Feature data
const FEATURES = [
  {
    id: 'feature-builder',
    icon: Wand2,
    title: 'Visual Resume Builder',
    description: 'Drag-and-drop sections, inline editing, and live preview with 5 professional styles. AI writes your summary, bullets, and skills for you.',
  },
  {
    id: 'feature-ats',
    icon: Target,
    title: 'ATS Optimization',
    description: 'Our AI analyzes your resume against ATS algorithms to ensure it passes automated screening systems used by 90% of employers.',
  },
  {
    id: 'feature-jobs',
    icon: Search,
    title: 'Job Board',
    description: 'Browse real job listings and tailor your resume for each role with one click. Integrated with live job feeds across industries.',
  },
  {
    id: 'feature-keywords',
    icon: Zap,
    title: 'Smart Keyword Matching',
    description: 'Automatically identifies missing keywords from job descriptions and suggests where to add them for maximum impact.',
  },
  {
    id: 'feature-cover-letter',
    icon: PenLine,
    title: 'AI Cover Letter Generator',
    description: 'Generate tailored cover letters in seconds from your resume and the job description. Personalized, professional, and ready to send.',
  },
  {
    id: 'feature-interview',
    icon: MessageSquare,
    title: 'AI Interview Prep',
    description: 'Practice with interview questions generated from your resume and target role, complete with suggested STAR-method answers.',
  },
  {
    id: 'feature-templates',
    icon: LayoutGrid,
    title: '20+ Resume Templates',
    description: 'Professionally optimized templates across 11 industries — from tech to healthcare. Pick one and start building in seconds.',
  },
  {
    id: 'feature-tracker',
    icon: Briefcase,
    title: 'Application Tracker',
    description: 'Track every application from applied to offer. Monitor response rates, interview stages, and stay organized across your job search.',
  },
  {
    id: 'feature-reoptimize',
    icon: RefreshCw,
    title: 'AI Resume Rewriter',
    description: 'Get AI-powered rewrites for weak bullet points using the STAR method with quantifiable metrics and impact statements.',
  },
];

// How It Works steps
const STEPS = [
  { id: 'step-1', step: '01', title: 'Upload Resume', desc: 'Paste your resume content or upload a file to get started.' },
  { id: 'step-2', step: '02', title: 'Get ATS Score', desc: 'Our AI analyzes your resume and provides a detailed score with suggestions.' },
  { id: 'step-3', step: '03', title: 'Optimize & Apply', desc: 'Apply the recommendations and watch your interview rate soar.' },
];

// Testimonials removed in favor of defensible Proof Stats band (see <ProofStats> below).
// If/when we collect real LinkedIn screenshots or video testimonials, restore the
// TestimonialCard pattern here and feed it real data. Until then, do NOT add
// stock-photo testimonials — readers smell them and conversion drops.

// Feature Card Component
const FeatureCard = ({ feature, index }) => {
  const Icon = feature.icon;
  const cardTransition = { ...scrollTransition, delay: index * 0.1 };
  return (
    <motion.div
      className="card-swiss p-8 bg-white"
      initial={fadeInUp}
      whileInView={fadeInUpVisible}
      transition={cardTransition}
      viewport={scrollViewport}
      data-testid={`feature-card-${index}`}
    >
      <div className="text-[#002fa7] mb-4"><Icon size={32} /></div>
      <h3 className="font-satoshi text-xl font-bold text-[#0f1115] mb-3">
        {feature.title}
      </h3>
      <p className="text-[#4b5563] leading-relaxed">{feature.description}</p>
    </motion.div>
  );
};

// Step Card Component
const StepCard = ({ step, index }) => {
  const cardTransition = { ...scrollTransition, delay: index * 0.1 };
  return (
  <motion.div
    className="text-center"
    initial={fadeInUp}
    whileInView={fadeInUpVisible}
    transition={cardTransition}
    viewport={scrollViewport}
  >
    <div className="text-6xl font-satoshi font-black text-[#e5e7eb] mb-4">
      {step.step}
    </div>
    <h3 className="font-satoshi text-xl font-bold text-[#0f1115] mb-2">
      {step.title}
    </h3>
    <p className="text-[#4b5563]">{step.desc}</p>
  </motion.div>
  );
};

// Testimonial Card removed — see comment above. Replaced by Proof Stats inline section.

// Hero Score Preview Component
const HeroScorePreview = () => (
  <div className="relative">
    <div className="border border-[#e5e7eb] p-6 bg-white">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6b7280]">
          ATS SCORE
        </span>
        <span className="text-4xl font-satoshi font-black text-[#10b981]">87</span>
      </div>
      <div className="h-2 bg-[#e5e7eb] w-full">
        <div className="h-full bg-[#10b981]" style={{ width: '87%' }}></div>
      </div>
      <div className="mt-6 space-y-3">
        <div className="flex items-center justify-between py-2 border-b border-[#e5e7eb]">
          <span className="text-sm text-[#4b5563]">Keywords Matched</span>
          <span className="text-sm font-semibold text-[#10b981]">12/14</span>
        </div>
        <div className="flex items-center justify-between py-2 border-b border-[#e5e7eb]">
          <span className="text-sm text-[#4b5563]">Format Score</span>
          <span className="text-sm font-semibold text-[#10b981]">95%</span>
        </div>
        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-[#4b5563]">Impact Score</span>
          <span className="text-sm font-semibold text-[#eab308]">78%</span>
        </div>
      </div>
    </div>
    <div className="absolute -bottom-4 -right-4 w-32 h-32 bg-[#002fa7] -z-10"></div>
  </div>
);

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  // Sets the browser-tab title at runtime. The static prerender bakes
  // the same title into build/index.html for first-paint SEO — this
  // hook keeps SPA navigations + dev preview in sync.
  useDocumentMeta({
    title: 'Ascend — AI Career Copilot for Resumes & ATS',
    description: 'The honest AI career copilot. Score your resume free in 30 seconds, see exactly which ATS keywords are missing, and let Sage rewrite weak bullets to match any job description.',
  });
  // A/B: hero urgency copy. v_2k = current ($2,000); v_1235 = consultant's
  // alternative ($1,235). 50/50 split. GA4 'ab_variant_assigned' fires once
  // per visitor; downstream conversion events will attribute via session.
  const heroUrgencyVariant = useABVariant('hero_urgency_copy', ['v_2k', 'v_1235']);

  return (
    <div className="min-h-screen" data-testid="landing-page">
      {/* Viewport-anchored scroll hint → Resume Studio */}
      <ScrollToStudioHint />

      {/* Hero Section */}
      <section className="pt-24 pb-16 lg:pt-32 lg:pb-24 border-b border-[#e5e7eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              className="lg:col-span-7"
              initial={fadeInUp}
              animate={heroAnimate}
              transition={heroTransition}
            >
              <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-4 block" data-testid="hero-eyebrow">
                THE HONEST AI CAREER COPILOT
              </span>
              <h1 className="font-satoshi text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-[#0f1115] leading-[1.1]">
                Land the role. <span className="text-[#002fa7]">Without the AI theatre.</span>
              </h1>
              <p className="mt-6 text-lg text-[#4b5563] max-w-xl leading-relaxed">
                Most "AI job copilots" hide the scoring, gate your data, and farm you for upsells. Ascend shows you the keywords, the math, and the rewrite — free, in 60 seconds. No signup wall.
              </p>
              <div className="mt-6 inline-flex items-start gap-2 px-3 py-2 bg-[#fef2f2] border-l-2 border-[#dc2626] rounded-sm" data-testid={`hero-urgency-${heroUrgencyVariant}`}>
                <span className="text-[#dc2626] font-semibold text-sm leading-snug">
                  {heroUrgencyVariant === 'v_1235'
                    ? 'Every unoptimized week = ~$1,235 in lost income.'
                    : 'Every week your resume sits unoptimized = ~$2,000 in lost income.'}
                  <span className="text-[#7f1d1d] font-normal"> Fix it in 60 seconds.</span>
                </span>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
                <Link
                  to="/free-ats-check"
                  className="btn-primary inline-flex items-center justify-center space-x-2"
                  data-testid="hero-cta"
                >
                  <span>Check My Resume — Free</span>
                  <ArrowRight size={20} />
                </Link>
                <span className="text-sm text-[#6b7280]" data-testid="hero-secondary-link">
                  {isAuthenticated ? (
                    <Link to="/dashboard" className="text-[#002fa7] font-semibold hover:underline">
                      Go to dashboard →
                    </Link>
                  ) : (
                    <>
                      Already have an account?{' '}
                      <Link to="/login" className="text-[#002fa7] font-semibold hover:underline">
                        Sign in →
                      </Link>
                    </>
                  )}
                </span>
              </div>
              <div className="mt-6 flex items-center space-x-6 text-sm text-[#6b7280]">
                <span className="flex items-center space-x-2">
                  <CheckCircle size={16} className="text-[#10b981]" />
                  <span>No credit card required</span>
                </span>
                <span className="flex items-center space-x-2">
                  <CheckCircle size={16} className="text-[#10b981]" />
                  <span>Instant score in 60s</span>
                </span>
              </div>
            </motion.div>
            <motion.div
              className="lg:col-span-5"
              initial={fadeInRight}
              animate={heroRightAnimate}
              transition={heroRightTransition}
            >
              <HeroScorePreview />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Honesty Manifesto — direct, named differentiator vs. AI-bro
          competitors (Jobright et al.). Three columns articulate the trust
          contract: transparent scoring, no data lock-in, no infinite
          freemium gating. This is the page's identity. */}
      <section className="py-12 lg:py-16 bg-white border-b border-[#e5e7eb]" data-testid="honesty-manifesto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-10">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#dc2626] mb-3 block">
              WHY WE'RE DIFFERENT
            </span>
            <h2 className="font-satoshi text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0f1115] leading-tight">
              The career-AI category is full of hype.<br />
              <span className="text-[#4b5563] font-medium">We chose the boring, honest path instead.</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#e5e7eb] border border-[#e5e7eb]">
            {[
              {
                id: 'manifesto-transparency',
                eyebrow: 'TRANSPARENCY',
                title: 'We show the math.',
                body: 'Every ATS score comes with the exact keywords matched, missing, and ranked. No black-box "compatibility %", no "trust the AI" hand-wave.',
              },
              {
                id: 'manifesto-free',
                eyebrow: 'NO SIGNUP WALL',
                title: 'Free actually means free.',
                body: 'Score your resume in 60 seconds without an account, without a credit card, without a "free trial" countdown. Forever.',
              },
              {
                id: 'manifesto-data',
                eyebrow: 'YOUR DATA',
                title: 'You own it. Always.',
                body: 'Export your resumes, your scores, your application history any time. No vendor lock-in. Cancel and your data leaves with you.',
              },
            ].map((m, i) => (
              <motion.div
                key={m.id}
                className="bg-white p-8 lg:p-10"
                initial={fadeInUp}
                whileInView={fadeInUpVisible}
                transition={{ ...scrollTransition, delay: i * 0.08 }}
                viewport={scrollViewport}
                data-testid={m.id}
              >
                <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#dc2626] block mb-3">
                  {m.eyebrow}
                </span>
                <h3 className="font-satoshi text-xl lg:text-2xl font-bold text-[#0f1115] mb-3">
                  {m.title}
                </h3>
                <p className="text-[#4b5563] leading-relaxed text-sm lg:text-base">
                  {m.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Follow Strip */}
      <SocialStrip />

      {/* Sage Spotlight — promoted out of the features grid (was 1 of 8 cards)
          to a dedicated band. Sage is Ascend's most distinctive moat: every
          ATS tool gives a score; almost none have an always-on copilot that
          already knows your resume. Star treatment, conversational preview,
          single CTA. Added 2026-05-23. */}
      <section className="py-16 lg:py-24 bg-[#0f1115] text-white border-b border-[#1f2937]" data-testid="sage-spotlight">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={fadeInUp}
            whileInView={fadeInUpVisible}
            transition={scrollTransition}
            viewport={scrollViewport}
          >
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#fbbf24] mb-4 block">
              MEET SAGE · AI COPILOT
            </span>
            <h2 className="font-satoshi text-4xl sm:text-5xl font-black tracking-tight leading-[1.05] mb-5">
              Like having a career coach<br />
              <span className="text-[#fbbf24]">who already read your resume.</span>
            </h2>
            <p className="text-[#cbd5e1] text-lg leading-relaxed mb-6">
              Most AI chat tools forget you the second you close the tab.
              Sage loads your latest resume on every conversation, remembers
              what you've worked on, and answers questions a $400/hr human
              coach would charge for — instantly, included in your subscription.
            </p>
            <ul className="space-y-3 mb-8 text-[#e5e7eb]">
              {[
                '"What should I fix on my resume this week?"',
                '"Rewrite my LinkedIn headline for staff-eng roles."',
                '"How do I follow up after Tuesday\'s interview?"',
                '"Which of my bullets are weakest and how do I fix them?"',
              ].map((q, i) => (
                <li key={i} className="flex items-start gap-2.5 text-sm">
                  <MessageSquare size={16} className="text-[#fbbf24] flex-shrink-0 mt-0.5" />
                  <span className="italic">{q}</span>
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap items-center gap-3">
              <Link
                to="/register?ref=landing-sage-spotlight&utm_source=landing&utm_medium=sage_band&utm_campaign=sage_spotlight"
                onClick={() => analytics.clickCTA('sage_spotlight_try', 'landing_sage_band')}
                className="inline-flex items-center gap-2 bg-[#fbbf24] text-[#0f1115] font-bold px-6 py-3 hover:bg-white transition-colors"
                data-testid="sage-spotlight-cta"
              >
                Try Sage free for 14 days <ArrowRight size={16} />
              </Link>
              <Link
                to="/sage"
                onClick={() => analytics.clickCTA('sage_spotlight_learn', 'landing_sage_band')}
                className="text-sm text-[#cbd5e1] hover:text-white underline underline-offset-4 decoration-[#fbbf24]/40"
                data-testid="sage-spotlight-learn"
              >
                See how Sage works →
              </Link>
            </div>
          </motion.div>

          {/* Conversation mockup — mirrors the SagePreview on /features */}
          <motion.div
            initial={fadeInUp}
            whileInView={fadeInUpVisible}
            transition={{ ...scrollTransition, delay: 0.15 }}
            viewport={scrollViewport}
            className="bg-[#1f2937] border border-[#374151] p-5 lg:p-6 shadow-2xl"
            data-testid="sage-spotlight-preview"
          >
            <div className="text-[11px] text-[#9ca3af] flex items-center gap-2 mb-4 font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-[#10b981] animate-pulse" />
              Sage loaded · <span className="text-[#fbbf24]">Senior Engineer Resume v3.docx</span>
            </div>
            <div className="space-y-3">
              <div className="ml-auto max-w-[80%] bg-[#002fa7] text-white px-4 py-2.5 text-sm rounded-sm">
                Should I lead with my Kubernetes work or system design?
              </div>
              <div className="mr-auto max-w-[88%] bg-[#0f1115] border border-[#374151] px-4 py-3 text-sm text-[#e5e7eb] leading-relaxed rounded-sm">
                <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#fbbf24] mb-1.5">
                  Sage
                </div>
                Lead with system design. Your Stripe checkout work shows clearer
                staff-level scope (cross-team coordination, 1.2M sessions) than
                your K8s migration. Move <span className="font-mono text-[#fbbf24]">"Reduced API p95 4.2s → 1.1s"</span>
                {' '}to your top bullet. That single line earns you the
                platform-eng interview.
              </div>
              <div className="ml-auto max-w-[60%] bg-[#002fa7] text-white px-4 py-2.5 text-sm rounded-sm">
                What should I fix this week?
              </div>
              <div className="mr-auto inline-flex items-center gap-1.5 text-xs text-[#6b7280]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#fbbf24] animate-pulse" />
                Sage is typing…
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 lg:py-24 bg-[#f4f5f7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-4 block">
              WHAT YOU GET
            </span>
            <h2 className="font-satoshi text-3xl sm:text-4xl font-bold tracking-tight text-[#0f1115]">
              Everything you need to land your dream job
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#e5e7eb]">
            {FEATURES.map((feature, index) => (
              <FeatureCard key={feature.id} feature={feature} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Resume Studio spotlight */}
      <ResumeStudioShowcase />

      {/* How It Works */}
      <section className="py-16 lg:py-24 border-b border-[#e5e7eb]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-4 block">
              HOW IT WORKS
            </span>
            <h2 className="font-satoshi text-3xl sm:text-4xl font-bold tracking-tight text-[#0f1115]">
              Three steps to your optimized resume
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {STEPS.map((step, index) => (
              <StepCard key={step.id} step={step} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Proof Stats — replaces stocky testimonials with defensible numbers */}
      <section className="py-16 lg:py-24 bg-[#0f1115]" data-testid="proof-stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#3b82f6] mb-4 block">
              THE NUMBERS
            </span>
            <h2 className="font-satoshi text-3xl sm:text-4xl font-bold tracking-tight text-white">
              Built on data, not on stock photos
            </h2>
            <p className="mt-4 text-[#9ca3af] max-w-2xl mx-auto">
              We don't pay actors. Here's what real users get from running their resume through Ascend.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#1f2937] border border-[#1f2937]">
            {[
              { id: 'stat-companies', stat: '500+', label: 'Companies our users target', sub: 'Tech, finance, healthcare, federal' },
              { id: 'stat-lift', stat: '+31', label: 'Avg ATS-score lift', sub: 'After 1 round of AI optimization' },
              { id: 'stat-pass', stat: '90%', label: 'of employers use ATS', sub: 'Your resume hits a robot first' },
              { id: 'stat-speed', stat: '60s', label: 'From paste to full score', sub: 'No account required to start' },
            ].map((s, i) => (
              <motion.div
                key={s.id}
                className="bg-[#0f1115] p-8 lg:p-10"
                initial={fadeInUp}
                whileInView={fadeInUpVisible}
                transition={{ ...scrollTransition, delay: i * 0.08 }}
                viewport={scrollViewport}
                data-testid={s.id}
              >
                <div className="font-satoshi text-5xl lg:text-6xl font-black text-white tracking-tighter mb-3">
                  {s.stat}
                </div>
                <div className="text-white font-semibold mb-1">{s.label}</div>
                <div className="text-[#9ca3af] text-sm leading-snug">{s.sub}</div>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-center text-xs text-[#6b7280] max-w-2xl mx-auto" data-testid="proof-disclaimer">
            ATS-lift figure based on internal sample of resumes scored before and after AI optimization. ATS adoption stat: Jobscan industry research, 2024.
          </p>
        </div>
      </section>

      {/* Pricing — transparent inline band so visitors see cost before signing up */}
      <section id="pricing" className="py-16 lg:py-24 border-b border-[#e5e7eb] bg-white" data-testid="landing-pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-4 block">
              PRICING
            </span>
            <h2 className="font-satoshi text-3xl sm:text-4xl font-bold tracking-tight text-[#0f1115]">
              Simple pricing. No surprises.
            </h2>
            <p className="mt-4 text-[#4b5563] max-w-2xl mx-auto">
              Start free. Upgrade only when you're ready to ship serious applications. Cancel anytime — your data stays.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#e5e7eb] max-w-5xl mx-auto" data-testid="landing-pricing-grid">
            {[
              {
                id: 'tier-free',
                name: 'Free',
                price: '$0',
                period: 'forever',
                tagline: 'Kick the tires.',
                features: ['1 resume optimization / month', 'Basic ATS scoring', 'Free ATS Score Checker', '1 cover letter / month', '1 interview prep / month', '3 AI rewrites / month'],
                cta: 'Start Free',
                ctaLink: '/register',
                highlight: false,
                badge: 'FREE FOREVER',
                badgeColor: '#10b981',
              },
              {
                id: 'tier-basic',
                name: 'Basic Yearly',
                price: '$190',
                period: 'year',
                priceSub: 'just $15.83/mo · save $38',
                tagline: 'For active job seekers.',
                features: ['10 resume optimizations / month', 'AI Cover Letter Generator', 'Job Board with match scoring', 'Application Tracker', '14-day free trial'],
                cta: 'Start 14-Day Trial',
                ctaLink: '/pricing?billing=yearly&plan=basic',
                highlight: false,
                badge: 'BEST VALUE',
                badgeColor: '#f59e0b',
              },
              {
                id: 'tier-pro',
                name: 'Pro Yearly',
                price: '$390',
                period: 'year',
                priceSub: 'just $32.50/mo · save $78',
                tagline: 'Land the role faster.',
                features: ['Unlimited resume optimizations', 'Unlimited AI Rewrites + Cover Letters', 'LinkedIn Profile Optimizer', 'Unlimited Sage AI Copilot', 'All 12 Studio themes + clean exports', '14-day free trial'],
                cta: 'Start 14-Day Trial',
                ctaLink: '/pricing?billing=yearly&plan=pro',
                highlight: true,
                badge: 'MOST POPULAR',
                badgeColor: '#002fa7',
                founding: true,
              },
            ].map((tier, i) => (
              <motion.div
                key={tier.id}
                className={`relative p-8 lg:p-10 ${tier.highlight ? 'bg-[#0f1115] text-white' : 'bg-white text-[#0f1115]'} ${i > 0 ? 'md:border-l border-[#e5e7eb]' : ''}`}
                initial={fadeInUp}
                whileInView={fadeInUpVisible}
                transition={{ ...scrollTransition, delay: i * 0.08 }}
                viewport={scrollViewport}
                data-testid={tier.id}
              >
                {tier.badge && (
                  <span
                    className="absolute top-0 right-0 text-white text-[10px] font-bold tracking-widest px-3 py-1.5 uppercase"
                    style={{ backgroundColor: tier.badgeColor || '#002fa7' }}
                    data-testid={`${tier.id}-badge`}
                  >
                    {tier.badge}
                  </span>
                )}
                <div className="font-satoshi text-2xl font-bold mb-1">{tier.name}</div>
                <p className={`text-sm mb-6 ${tier.highlight ? 'text-[#9ca3af]' : 'text-[#6b7280]'}`}>{tier.tagline}</p>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="font-satoshi text-5xl font-black tracking-tighter">{tier.price}</span>
                  <span className={tier.highlight ? 'text-[#9ca3af]' : 'text-[#6b7280]'}>/ {tier.period}</span>
                </div>
                {tier.priceSub && (
                  <p className={`text-xs mb-6 ${tier.highlight ? 'text-[#3b82f6]' : 'text-[#002fa7]'} font-semibold`}>{tier.priceSub}</p>
                )}
                {tier.founding && (
                  <div className="mb-6 px-3 py-2 bg-[#1e3a8a]/30 border-l-2 border-[#fbbf24]" data-testid={`${tier.id}-founding`}>
                    <span className="text-[#fbbf24] text-xs font-bold tracking-wide uppercase">Founding member</span>
                    <p className="text-[#e0e7ff] text-sm mt-0.5">40% off auto-applied at checkout. No code needed.</p>
                  </div>
                )}
                {!tier.priceSub && !tier.founding && <div className="mb-6" />}
                <ul className="space-y-3 mb-8">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <CheckCircle size={16} className={tier.highlight ? 'text-[#10b981] flex-shrink-0 mt-0.5' : 'text-[#10b981] flex-shrink-0 mt-0.5'} />
                      <span className={tier.highlight ? 'text-[#e5e7eb]' : 'text-[#374151]'}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={tier.ctaLink}
                  className={`block w-full text-center py-3 px-4 font-semibold transition-colors ${
                    tier.highlight
                      ? 'bg-[#fbbf24] text-[#0f1115] hover:bg-[#f59e0b]'
                      : 'bg-[#002fa7] text-white hover:bg-[#001f6e]'
                  }`}
                  data-testid={`${tier.id}-cta`}
                >
                  {tier.cta}
                </Link>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-center text-sm text-[#6b7280]">
            Need more? See <Link to="/pricing" className="text-[#002fa7] font-semibold hover:underline" data-testid="see-full-pricing">full pricing & credit packs →</Link>
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 border-b border-[#e5e7eb]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-satoshi text-3xl sm:text-4xl font-bold tracking-tight text-[#0f1115] mb-6">
            Ready to land your dream job?
          </h2>
          <p className="text-lg text-[#4b5563] mb-8 max-w-2xl mx-auto">
            Run a free ATS check on your resume right now. See your score, your gaps, and exactly what to fix — no account needed.
          </p>
          <Link
            to="/free-ats-check"
            className="btn-primary inline-flex items-center space-x-2"
            data-testid="cta-free-check"
          >
            <span>Check My Resume — Free</span>
            <ArrowRight size={20} />
          </Link>
          <p className="mt-4 text-sm text-[#6b7280]">
            {isAuthenticated ? (
              <Link to="/dashboard" className="text-[#002fa7] font-semibold hover:underline" data-testid="cta-go-dashboard">Go to your dashboard →</Link>
            ) : (
              <>Already a member? <Link to="/login" className="text-[#002fa7] font-semibold hover:underline" data-testid="cta-sign-in">Sign in →</Link></>
            )}
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-16 lg:py-24 bg-[#f4f5f7]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#002fa7] mb-4 block">
              FAQ
            </span>
            <h2 className="font-satoshi text-3xl sm:text-4xl font-bold tracking-tight text-[#0f1115]">
              Frequently asked questions about ATS resume optimization
            </h2>
          </div>
          <div className="space-y-0 border border-[#e5e7eb]">
            {[
              {
                id: 'faq-ats-checker',
                q: 'What is an ATS resume checker?',
                a: 'An ATS (Applicant Tracking System) resume checker analyzes your resume against the same algorithms that employers use to filter job applications. It scores your resume\'s compatibility, identifies missing keywords, and suggests improvements to help you pass automated screening and land more interviews.',
              },
              {
                id: 'faq-free-check',
                q: 'How do I check my resume ATS score for free?',
                a: 'You can check your ATS score for free using our Free ATS Score Checker. Simply paste your resume text, enter the job title you\'re targeting, and get an instant score with specific strengths, critical gaps, and quick fixes. No account or credit card required.',
              },
              {
                id: 'faq-rejected',
                q: 'Why is my resume getting rejected before anyone reads it?',
                a: 'About 75% of resumes are filtered out by ATS before a human ever sees them. Common reasons include: missing keywords from the job description, fancy formatting that ATS can\'t parse (tables, columns, graphics), creative section headers instead of standard ones, and submitting in unsupported file formats.',
              },
              {
                id: 'faq-optimize',
                q: 'How can I optimize my resume for applicant tracking systems?',
                a: 'Use simple single-column formatting, mirror exact keywords from the job description, use standard section headers (Experience, Education, Skills), include quantifiable achievements with numbers, and submit as .docx format. Our AI tool analyzes your resume and provides specific recommendations for your target role.',
              },
              {
                id: 'faq-percentage',
                q: 'What percentage of companies use ATS to screen resumes?',
                a: 'Over 90% of Fortune 500 companies and approximately 75% of all employers use ATS to screen resumes. This means the majority of job applications are filtered by software before a human recruiter ever reads them.',
              },
            ].map((faq) => (
              <details key={faq.id} className="group bg-white border-b border-[#e5e7eb] last:border-b-0" data-testid={`faq-item-${faq.id}`}>
                <summary className="flex items-center justify-between p-6 cursor-pointer font-satoshi font-bold text-[#0f1115] hover:text-[#002fa7] transition-colors">
                  {faq.q}
                  <span className="ml-4 text-[#6b7280] group-open:rotate-45 transition-transform text-xl">+</span>
                </summary>
                <p className="px-6 pb-6 text-[#4b5563] leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
